// src/oura/delivery/RequestRows.tsx
//
// The client's open change requests on Delivery, so a request staff added on
// the Client page (or the client asked in Oura) is on the same list as the
// plan's work. A request filed as the month's small change is G5's work, so
// G5's line names it; one not filed yet says it can be. Opening a request
// opens its drawer on the Client page, where staff reply, file it under G5
// and mark it done.

import { MessageSquarePlusIcon } from "lucide-react";
import type { ChangeRequest } from "@service/aeo/clientReview";
import type { Deliverable } from "@service/aeo/delivery";
import { askedText, requesterName, requestIsOurs } from "../components/client/changeRequests";
import { FamilyChip, GroupFold, IconSquare, ListRow, TYPE } from "../ui";
import { cn } from "../lib/cn";

const TITLE_MAX = 110;

/** The request as one line: Oura's title when it has one, else the client's words. */
export function requestTitle(c: Pick<ChangeRequest, "note"> & { title?: string | null }): string {
    const t = (c.title?.trim() || c.note.trim()).replace(/\s+/g, " ");
    return t.length > TITLE_MAX ? `${t.slice(0, TITLE_MAX - 1)}…` : t;
}

/** The open requests, newest first. */
export function openRequests(requests: readonly ChangeRequest[] | null | undefined): ChangeRequest[] {
    return (requests ?? []).filter(requestIsOurs).sort((a, b) => (a.at < b.at ? 1 : -1));
}

/** The request filed as this plan month's small change, if one is. */
export function filedFor(requests: readonly ChangeRequest[] | null | undefined, month: number): ChangeRequest | null {
    return (requests ?? []).find((c) => c.smallChange?.termMonth === month) ?? null;
}

/**
 * G5's line when requests say more than the server's step does: the request
 * it is working on, or an open one that could be filed. Null keeps the line
 * as the server sent it.
 */
export function smallChangeLine(d: Deliverable, requests: readonly ChangeRequest[] | null | undefined): string | null {
    if (d.code !== "G5" || d.group === "done") return null;
    const filed = filedFor(requests, d.month);
    if (filed) return `“${requestTitle(filed)}”`;
    const open = openRequests(requests).filter((c) => !c.smallChange);
    if (open.length === 0) return null;
    return open.length === 1 ? "1 open request can be filed as this month’s small change" : `${open.length} open requests, one can be filed as this month’s small change`;
}

export function RequestRows({
    businessId,
    requests,
    month,
    onOpen,
}: {
    businessId: string;
    requests: readonly ChangeRequest[] | null | undefined;
    /** The current plan month, to tell this month's small change from another's. */
    month: number;
    onOpen: (requestId: string) => void;
}) {
    const open = openRequests(requests);
    if (open.length === 0) return null;
    return (
        <GroupFold id={`delivery.${businessId}.requests`} tone="blue" title="Client requests" count={open.length} lead="square">
            {open.map((c) => {
                const g5 = c.smallChange?.termMonth === month;
                const sub = [requesterName(c), c.where, g5 ? "This month’s small change" : c.smallChange ? "Filed as a small change" : null].filter(Boolean).join(" · ");
                return (
                    <ListRow
                        key={c.id}
                        icon={g5 ? <FamilyChip family="g">G5</FamilyChip> : <IconSquare tone="blue" icon={MessageSquarePlusIcon} className="h-6 w-6 rounded-lg" />}
                        lead="square"
                        title={requestTitle(c)}
                        sub={<span className={cn(TYPE.tc, "text-gray-500")}>{sub}</span>}
                        meta={askedText(c.at) ?? undefined}
                        onClick={() => onOpen(c.id)}
                        stackMeta
                        data-request={c.id}
                    />
                );
            })}
        </GroupFold>
    );
}
